import type { JSX, ReactNode } from 'react';
import { HookFormInput } from 'ts/commons/forms/HookFormInput';
import { HookFormRadio } from 'ts/commons/forms/HookFormRadio';
import { RegexUtils } from 'ts/commons/RegexUtils';
import { FormGroup } from 'ts/components/Form';

/** Values provided by the RegexFilter component. */
export type RegexFilterValues = {
	/** A regex filter for the log entries. */
	regex: string;

	/** Whether regex includes or excludes log entries */
	regexExclude: 'include' | 'exclude';
};

/** Props for the regex filter. */
type RegexFilterProps = {
	filterTooltip: string;
	children?: ReactNode;
};

/**
 * A combination of a controlled regex input field and a group of radio buttons that allow to specify whether items
 * matching the pattern should be included or excluded. The children of the component will be inserted after the input.
 * The filter expects to be wrapped in a FormProvider for handling the input validation.
 */
export function RegexFilter({ filterTooltip, children }: RegexFilterProps): JSX.Element {
	return (
		<FormGroup grouped as="fieldset">
			<HookFormInput<RegexFilterValues>
				fluid
				name="regex"
				style={{ cursor: 'help' }}
				label="Regex"
				id="regex-filter"
				title={filterTooltip}
				placeholder="e.g.: ((in)|(out))put"
				rules={{ validate: RegexUtils.validateJsRegex }}
			/>
			{children ?? null}
			<FormGroup grouped as="fieldset">
				<HookFormRadio name="regexExclude" id="include-radio" value="include" label="Include matches" />
				<HookFormRadio name="regexExclude" id="exclude-radio" value="exclude" label="Exclude matches" />
			</FormGroup>
		</FormGroup>
	);
}
